import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import { ContentContainer, GoogleDFP, PageRail } from '@surfline/quiver-react';
import { postedDate } from '@surfline/web-common/dist/utils/formatReportDate';
import RegionForecastUpdate from '../RegionForecastUpdate';
import InactiveMessage from '../InactiveMessage';
import ForecastDataVisualizer from '../../containers/ForecastDataVisualizer';
import CTAProvider from '../../containers/CTAProvider';
import MobileForecastCTA from '../MobileForecastCTA/MobileForecastCTA';
import subregionAdConfigPropType from '../../propTypes/subregionAdConfig';
import devicePropTypes from '../../propTypes/device';
import summaryPropType from '../../propTypes/summary';
import overviewDataPropShape from '../../propTypes/subregionOverviewData';
import loadAdConfig from '../../utils/adConfig';
import config from '../../config';

const className = highlights =>
  classNames({
    'sl-regional-forecast': true,
    'sl-regional-forecast--no-highlights': highlights.length === 0,
  });

class RegionalForecast extends Component {
  constructor() {
    super();
    this.state = { mobile: false };
  }

  componentDidMount() {
    this.setMobileState(window.innerWidth);
  }

  getCurrentCondition = summary => {
    if (!summary?.days) {
      return null;
    }
    const conditionDay = summary?.days[0];
    const pm = new Date().getHours() > 12;
    const currentCondition = conditionDay[pm ? 'pm' : 'am'].rating;
    return currentCondition;
  };

  setMobileState = innerWidth => {
    if (innerWidth < config.tabletLargeWidth) this.setState({ mobile: true });
  };

  render() {
    const { mobile } = this.state;
    const {
      isPremium,
      primarySpotId,
      overviewData: { _id: subregionId, name: subregionName, forecastSummary, spots, timestamp },
      subregionAdConfig,
      utcOffset,
      abbrTimezone,
      doTrackEvent,
      doClickedSubscribeCTA,
      device,
      summary,
    } = this.props;
    const segmentProperties = {
      ctaLocation: 'mobile forecast go premium',
      pageName: 'Regional Forecast',
      subregionId,
    };
    const primarySpot = spots.find(spot => spot._id === primarySpotId);

    return (
      <div className={className(forecastSummary.highlights)}>
        {forecastSummary.forecastStatus.status === 'active' &&
        (forecastSummary.hasHighlights || forecastSummary.hasBets) ? (
          <ContentContainer>
            <PageRail side="left">
              <RegionForecastUpdate
                forecastSummary={forecastSummary}
                subregionId={subregionId}
                subregionName={subregionName}
                timestamp={timestamp}
                condition={this.getCurrentCondition(summary)}
                utcOffset={utcOffset}
                abbrTimezone={abbrTimezone}
                doTrackEvent={doTrackEvent}
                doClickedSubscribeCTA={doClickedSubscribeCTA}
                isPremium={isPremium}
                clickEventProps={{
                  category: 'regionForecast',
                  linkLocation: 'regional forecast',
                }}
              />
            </PageRail>
            <PageRail side="right">
              <div className="sl-regional-forecast__ad--top">
                <GoogleDFP
                  adConfig={loadAdConfig(
                    'forecastBox',
                    subregionAdConfig.adTargets,
                    subregionAdConfig.userEntitlements,
                    subregionAdConfig.isUser,
                    'SUBREGION'
                  )}
                />
              </div>
            </PageRail>
          </ContentContainer>
        ) : null}
        {forecastSummary.forecastStatus.status === 'inactive' &&
        forecastSummary.forecastStatus.inactiveMessage ? (
          <ContentContainer>
            <InactiveMessage message={forecastSummary.forecastStatus.inactiveMessage} />
          </ContentContainer>
        ) : null}
        {forecastSummary.forecastStatus.status !== 'active' ||
        (!forecastSummary.hasHighlights && !forecastSummary.hasBets) ||
        !isPremium ||
        mobile ? (
          <ContentContainer>
            <div className="sl-regional-forecast__ad--centered">
              <GoogleDFP
                adConfig={loadAdConfig(
                  'forecastHorizontal',
                  subregionAdConfig.adTargets,
                  subregionAdConfig.userEntitlements,
                  subregionAdConfig.isUser,
                  'SUBREGION'
                )}
              />
            </div>
          </ContentContainer>
        ) : null}
        <ContentContainer>
          {!isPremium ? (
            <div className="sl-regional-forecast__mobile-cta">
              <CTAProvider segmentProperties={segmentProperties}>
                <MobileForecastCTA />
              </CTAProvider>
            </div>
          ) : null}
          {primarySpotId && subregionId ? (
            <div className="sl-regional-forecast__charts">
              <ForecastDataVisualizer
                type="REGIONAL"
                category="regional forecast"
                pageName="Regional Forecast"
                spotId={primarySpotId}
                subregionId={subregionId}
                spotName={primarySpot ? primarySpot.name : null}
                utcOffset={utcOffset}
                lastUpdate={postedDate(timestamp, utcOffset, abbrTimezone)}
                extended
                canToggleDataView
                device={device}
              />
            </div>
          ) : null}
          <div className="sl-regional-forecast__ad--bottom">
            <GoogleDFP
              adConfig={loadAdConfig(
                'forecastBottom',
                subregionAdConfig.adTargets,
                subregionAdConfig.userEntitlements,
                subregionAdConfig.isUser,
                'SUBREGION'
              )}
            />
          </div>
        </ContentContainer>
      </div>
    );
  }
}

RegionalForecast.propTypes = {
  primarySpotId: PropTypes.string,
  overviewData: overviewDataPropShape.isRequired,
  isPremium: PropTypes.bool,
  subregionAdConfig: subregionAdConfigPropType.isRequired,
  utcOffset: PropTypes.number.isRequired,
  abbrTimezone: PropTypes.string.isRequired,
  doTrackEvent: PropTypes.func.isRequired,
  doClickedSubscribeCTA: PropTypes.func.isRequired,
  device: devicePropTypes.isRequired,
  summary: summaryPropType.isRequired,
};

RegionalForecast.defaultProps = {
  primarySpotId: null,
  isPremium: false,
};

export default RegionalForecast;
